import React from "react";
import _get from "lodash.get";

import Loader from "components/common/Loader";
import RiskProfileContainer from "./RiskProfile/";
import ProfileEditMenuContainer from "./Profile/";

import JarvisSubscriptionPlans from "components/JarvisSubscriptionPlans";
import { config } from "config";
import JarvisCard from "components/JarvisCard";
import { withRouter } from "react-router-dom";
import ScheduleCallModal from "components/common/ScheduleCallModal";
import Stack from "components/common/Stack";
import { masterApis } from "stores/master/masterApis";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";
import { performAppLogout } from "hooks/useAppLogout";
import AllInOneProfilePage from "components/allInOneDashboard/profile/AllInOneProfilePage";
import LogoutConfirmModal from "components/allInOneDashboard/profile/LogoutConfirmModal";

const ESSENTIALS_KEYS = ["portfolio", "rms", "onestock"];
const PRIVATE_WEALTH_KEYS = ["prime", "sentiment"];

const PRODUCT_ICON_MAP = {
  portfolio: "/images/product-info/portfolio-3d.png",
  rms: "/images/product-info/protect-3d.png",
  onestock: "/images/product-info/onestock-3d.png",
  prime: "/images/product-info/prime-3d.png",
  sentiment: "/images/product-info/sentiment-3d.png",
};

const INVEST_MORE_MAP = {
  portfolio: { amount: 10000, href: "/jarvis-portfolio/invest-more" },
  prime: { amount: 200000, href: "/jarvis-prime/invest-more" },
};

const PROFILE_MENU_ICONS = {
  blog: "/images/profile-v2/blog-icon.svg",
  marketUpdates: "/images/profile-v2/market-updates-icon.svg",
  newsArticles: "/images/profile-v2/news-articles-icon.svg",
  helpSupport: "/images/profile-v2/help-circle-icon.svg",
  logOut: "/images/profile-v2/log-out-icon.svg",
};

const screenTypes = {
  VIEW_PROFILE: "VIEW_PROFILE",
  SUBSCRIPTION_PLANS: "SUBSCRIPTION_PLANS",
};

const planFrequencyMessage = {
  MONTHLY:
    "Your Jarvis portfolio subscription auto-renews every month. <br/> You can cancel subscription any time.",
  HALF_YEARLY:
    "Your Jarvis portfolio subscription auto-renews every Half-yearly. <br/> You can cancel subscription any time.",
  YEARLY:
    "Your Jarvis portfolio subscription auto-renews every year. <br/> You can cancel subscription any time.",
  QUARTERLY:
    "Your Jarvis portfolio subscription auto-renews every quarter. <br/> You can cancel subscription any time.",
};

class Profile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeScreenType: screenTypes.VIEW_PROFILE,
      selectedFrequency: null,
      selectedPlanData: null,
      openRMModal: false,
      openLogoutModal: false,
    };
  }

  componentDidMount() {
    if (
      !this.props.profile.length ||
      _get(this.props.location, "state.paymentStatus", "") === "SUCCEED"
    ) {
      this.props.getProfileDataAction();
    }
    if (
      !this.props.subscriptionPlans.length &&
      !this.props.isSubscriptionPlansLoading &&
      !this.props.isSubscriptionPlansCalled
    ) {
      this.props.getSubscriptionPlansAction({
        productCode: config.productCodes.PORTFOLIO || config.productCodes.PRIME,
      });
    }
    this.props.getProductsSubscriptionAction();
  }

  componentDidUpdate() {
    const subscriptionFrequencies = this.getSubscriptionPlanFrequencies();
    if (subscriptionFrequencies.length && !this.state.selectedFrequency) {
      this.handleFrequencyChange({
        selectedFrequency: subscriptionFrequencies[0],
      });
    }
  }

  getSubscriptionPlanFrequencies = () => {
    const planFrequency = [];
    if (this.props.subscriptionPlans.length) {
      this.props.subscriptionPlans.forEach((plan) => {
        if (!planFrequency.includes(plan.planFrequency)) {
          planFrequency.push(plan.planFrequency);
        }
      });
    }
    return planFrequency;
  };

  handleBackToProfile = () => {
    this.setState({ activeScreenType: screenTypes.VIEW_PROFILE });
  };

  handleFrequencyChange = ({ selectedFrequency }) => {
    this.setState({ selectedFrequency });
  };

  handlePlanSelection = (planData) => {
    this.setState({ selectedPlanData: planData, confirmDialogOpen: true });
  };

  handlePaymentClick = () => {
    this.setState({ selectedPlanData: null, confirmDialogOpen: false });
    this.props.history.push({
      pathname: "/customer/subscription",
      state: {
        returnURI: "/customer/profile",
        returnURIonSuccess: "/customer/dashboard",
        productCode: config.productCodes.PORTFOLIO || config.productCodes.PRIME,
        planId: this.state.selectedPlanData.planId,
        paymentType: "RENEWAL",
      },
    });
  };

  handlePlanAndPricingClick = () => {
    if (!Object.keys(this.props.renewalPricingDetails).length) {
      this.props.getRenewSubscriptionAction();
    }
    this.props.history.push({
      pathname: "/customer/plan-and-pricing",
      state: {
        returnURI: "/customer/dashboard",
        paymentType: "RENEWAL",
      },
    });
  };

  handleOnActionPress = (productCode) => {
    localStorage.setItem("activeProduct", JSON.stringify(productCode));
    this.props.history.push({
      pathname: `/customer/profile/subscription`,
      state: {
        productCode,
      },
    });
  };

  openRMModal = () => {
    this.setState({ openRMModal: true });
  };

  closeRMModal = () => {
    this.setState({ openRMModal: false });
  };

  handleRMSchedule = async (data) => {
    const payload = {
      scheduleDate: dateTimeHelper.sipDate(new Date(data.date)),
      scheduleTime: data.time.slice(0, 5),
    };

    try {
      this.setState({ loading: true });
      await masterApis.submitScheduleCall(payload);
      this.setState({ openRMModal: false });
    } catch (error) {
      console.error("Schedule error:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  openLogoutModal = () => {
    this.setState({ openLogoutModal: true });
  };

  closeLogoutModal = () => {
    this.setState({ openLogoutModal: false });
  };

  handleLogout = () => {
    performAppLogout(this.props.logoutAction, this.props.history);
  };

  confirmLogout = () => {
    this.setState({ openLogoutModal: false });
    this.handleLogout();
  };

  buildPlanGroup = (productConfigs, keys) => {
    const groupConfigs = productConfigs.filter((c) => keys.includes(c.key));
    return {
      activeCount: groupConfigs.filter(
        (c) => c.data?.planStatus === "ACTIVATED",
      ).length,
      plans: groupConfigs
        .filter((c) => c.label)
        .map((c) => ({
          key: c.key,
          icon: PRODUCT_ICON_MAP[c.key],
          name: c.label,
          planStatus: c.data?.planStatus,
          billingDate: c.data?.expiryDate,
          price:
            c.data?.planStatus === "ACTIVATED" ? c.data?.planPrice : null,
          frequency:
            c.data?.planStatus === "ACTIVATED"
              ? formatHelper.getPaymentFrequencyLabel(
                  c.data?.paymentFrequency,
                )
              : null,
          onManageClick: c.onActionPress,
          investMore: INVEST_MORE_MAP[c.key] || null,
        })),
    };
  };

  render() {
    const {
      subscriptionPlans,
      renewalPricingDetails,
      productSubscriptionList,
    } = this.props;

    let { rmsData, portfolioData, onestockData, primeData, sentimentData } =
      productSubscriptionList;

    const {
      activeScreenType,
      selectedFrequency,
      selectedPlanData,
      confirmDialogOpen,
    } = this.state;

    const productConfigs = [
      {
        key: "portfolio",
        label: portfolioData?.planName,
        data: portfolioData,
        onActionPress: () =>
          this.handleOnActionPress(config.productCodes.PORTFOLIO),
        showInvestmentStrategy: this.props.isPortfolioOnboardingCompleted,
      },
      {
        key: "prime",
        label: primeData?.planName,
        data: primeData,
        onActionPress: () =>
          this.handleOnActionPress(config.productCodes.PRIME),
        showInvestmentStrategy: this.props.isPrimeOnboardingCompleted,
      },
      {
        key: "rms",
        label: rmsData?.planName,
        data: rmsData,
        onActionPress: () => this.handleOnActionPress(config.productCodes.RMS),
        forProtect: true,
      },
      {
        key: "onestock",
        label: onestockData?.planName,
        data: onestockData,
        onActionPress: () =>
          this.handleOnActionPress(config.productCodes.ONESTOCK),
        forProtect: true,
      },
      {
        key: "sentiment",
        label: sentimentData?.planName,
        data: sentimentData,
        onActionPress: () =>
          this.handleOnActionPress(config.productCodes.SENTIMENT),
        forProtect: true,
      },
    ];

    // Keep layout/nav mounted; only block the content area while loading.
    if (this.props.isProfileLoading) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: "40vh", py: 6, position: "relative" }}
        >
          <Loader position="relative" loading />
        </Stack>
      );
    }

    if (activeScreenType === screenTypes.SUBSCRIPTION_PLANS) {
      const subscriptionFrequencies = this.getSubscriptionPlanFrequencies();
      return (
        <JarvisCard>
          <JarvisSubscriptionPlans
            onBackClick={this.handleBackToProfile}
            subscriptionPlans={subscriptionPlans}
            subscriptionFrequencies={subscriptionFrequencies}
            onFrequencyChange={this.handleFrequencyChange}
            onPlanSelect={this.handlePlanSelection}
            selectedFrequency={selectedFrequency}
            currentPortfolioCount={renewalPricingDetails.currentPortfolioCount}
            currentStockCount={renewalPricingDetails.currentStockCount}
            currentInvestmentAmount={renewalPricingDetails.investmentAmount}
            selectedPlanData={selectedPlanData}
            confirmDialog={{
              open: confirmDialogOpen,
              title: `Selected Plan: <span style="white-space: nowrap">${selectedPlanData?.planName}</span>`,
              content: planFrequencyMessage[selectedFrequency],
              onConfirm: () => this.handlePaymentClick(),
              onCancel: () =>
                this.setState({
                  confirmDialogOpen: false,
                  selectedPlanData: null,
                }),
            }}
          />
        </JarvisCard>
      );
    }

    const { profile } = this.props;

    const essentialsGroup = this.buildPlanGroup(
      productConfigs,
      ESSENTIALS_KEYS,
    );
    const privateWealthGroup = this.buildPlanGroup(
      productConfigs,
      PRIVATE_WEALTH_KEYS,
    );

    const menuItems = [
      {
        icon: PROFILE_MENU_ICONS.blog,
        label: "Blog",
        onClick: () => this.props.history.push("/customer/blogs"),
      },
      {
        icon: PROFILE_MENU_ICONS.marketUpdates,
        label: "Market Updates",
        onClick: () =>
          this.props.history.push("/knowledge-centre/market-updates"),
      },
      {
        icon: PROFILE_MENU_ICONS.newsArticles,
        label: "News & Articles",
        onClick: () =>
          this.props.history.push("/knowledge-centre/news-articles"),
      },
      {
        icon: PROFILE_MENU_ICONS.helpSupport,
        label: "Help & Support",
        onClick: () =>
          this.props.history.push("/knowledge-centre/help-support"),
      },
      {
        icon: PROFILE_MENU_ICONS.logOut,
        label: "Log Out",
        danger: true,
        onClick: this.openLogoutModal,
      },
    ];

    return (
      <>
        <AllInOneProfilePage
          identity={{
            avatarSrc: profile.profilePic,
            fullName: profile.fullName,
            isVerified: !!profile.isKYCCompleted,
            email: profile.email,
            mobileNo: profile.mobileNo,
            topRightSlot: <ProfileEditMenuContainer />,
          }}
          menuItems={menuItems}
          onContactRM={this.openRMModal}
          riskProfileSlot={<RiskProfileContainer />}
          onViewBillingHistory={() => {
            // Explicit empty state so Manage's productCode never leaks in.
            // Do not touch localStorage activeProduct (app-wide product context).
            this.props.history.push({
              pathname: "/customer/profile/subscription",
              state: {},
            });
          }}
          essentialsGroup={essentialsGroup}
          privateWealthGroup={privateWealthGroup}
        />

        <ScheduleCallModal
          open={this.state.openRMModal}
          onClose={this.closeRMModal}
        />

        <LogoutConfirmModal
          open={this.state.openLogoutModal}
          onClose={this.closeLogoutModal}
          onConfirm={this.confirmLogout}
        />
      </>
    );
  }
}

export default withRouter(Profile);
